import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Facebook, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSettings } from '@/context/SettingsContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0538adf5-edcf-4e64-bcf3-6c7fb647f534/artifacts/2082kics_D75441D7-BE59-4926-BF65-8E7B3FDC121A.jpg";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { settings } = useSettings();
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/castles', label: 'Bouncy Castles' },
    { href: '/about', label: 'About Us' },
    { href: '/safety', label: 'Safety' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-black shadow-md" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Master Castles" 
              className="h-14 w-auto"
              loading="eager"
              fetchpriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-nunito font-semibold transition-colors ${
                  isActive(link.href)
                    ? 'text-[#4ECDC4]'
                    : 'text-white hover:text-[#4ECDC4]'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {/* Social Icons */}
            <div className="hidden sm:flex items-center space-x-2">
              <a
                href={settings?.facebook_url || "https://www.facebook.com/share/1DrqQpsLXB/?mibextid=wwXIfr"}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={settings?.instagram_url || "https://www.instagram.com/mastercastles"}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 text-white hover:opacity-90 transition-opacity"
                data-testid="social-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>

            {/* Book Now Button */}
            <Link to="/booking">
              <Button 
                className="hidden sm:flex rounded-full bg-gradient-to-r from-[#E63954] to-[#D62E48] text-white font-outfit font-bold px-6 hover:from-[#D62E48] hover:to-[#C0263E] shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                data-testid="book-now-btn"
              >
                Book Now
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-800 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-700" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-nunito font-semibold py-2 ${
                    isActive(link.href) ? 'text-[#4ECDC4]' : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full rounded-full bg-gradient-to-r from-[#E63954] to-[#D62E48] text-white font-outfit font-bold mt-2">
                  Book Now
                </Button>
              </Link>
              <div className="flex items-center space-x-3 pt-2">
                <a
                  href={settings?.facebook_url || "https://www.facebook.com/share/1DrqQpsLXB/?mibextid=wwXIfr"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-blue-600 text-white"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href={settings?.instagram_url || "https://www.instagram.com/mastercastles"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 text-white"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export function Footer() {
  const { settings } = useSettings();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/castles', label: 'Bouncy Castles' },
    { href: '/about', label: 'About Us' },
    { href: '/safety', label: 'Safety' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Grass decoration */}
      <div className="h-8 bg-gradient-to-b from-green-500/20 to-slate-900" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <img 
              src={LOGO_URL} 
              alt="Master Castles" 
              className="h-16 w-auto rounded-lg mb-4"
            />
            <p className="text-slate-400 text-sm font-nunito">
              {settings?.footer_text || "Making parties unforgettable with safe, clean bouncy castles."}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href} 
                    className="text-slate-400 hover:text-white transition-colors font-nunito text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-2 text-slate-400 text-sm font-nunito">
              {settings?.phone && (
                <li>
                  <a href={`tel:${settings.phone}`} className="hover:text-white transition-colors">
                    {settings.phone}
                  </a>
                </li>
              )}
              {settings?.email && (
                <li>
                  <a href={`mailto:${settings.email}`} className="hover:text-white transition-colors">
                    {settings.email}
                  </a>
                </li>
              )}
              {settings?.address && <li>{settings.address}</li>}
              {settings?.business_hours && <li>{settings.business_hours}</li>}
            </ul>
          </div>

          {/* Social & CTA */}
          <div>
            <h4 className="font-outfit font-bold text-lg mb-4">Follow Us</h4>
            <div className="flex items-center space-x-3 mb-6">
              <a
                href={settings?.facebook_url || "https://www.facebook.com/share/1DrqQpsLXB/?mibextid=wwXIfr"}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={settings?.instagram_url || "https://www.instagram.com/mastercastles"}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 text-white hover:opacity-90 transition-opacity"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
            <Link to="/booking">
              <Button className="w-full rounded-full bg-gradient-to-r from-[#E63954] to-[#D62E48] text-white font-outfit font-bold hover:from-[#D62E48] hover:to-[#C0263E]">
                Book Now
              </Button>
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Master Castles. All rights reserved.</p>
          <div className="flex items-center space-x-4 mt-4 sm:mt-0">
            <Link to="/safety" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/safety" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children, className = "" }) {
  return (
    <div className={`min-h-screen flex flex-col ${className}`}>
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
